import React, { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getUpcomingEvents } from "../services/firebase";

const formatEventDate = (timestamp) => {
  const date = new Date(timestamp.seconds * 1000);
  const dayName = date.toLocaleDateString([], { weekday: "short" });
  const monthDay = date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dayName}, ${monthDay} · ${time}`;
};

const EventRow = ({ event }) => {
  const navigate = useNavigate();
  const imageUrl = event.mainImage?.src;

  return (
    <ButtonBase
      onClick={() => navigate(`/events/${event.id}`)}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        textAlign: "left",
        px: 2,
        py: 1.5,
        gap: 2,
        transition: "background-color 0.15s",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.03)",
        },
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          minWidth: 64,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "rgba(0,0,0,0.06)",
        }}
      >
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt={event.name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {event.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.25, lineHeight: 1.3 }}
        >
          {event.nextInstance
            ? formatEventDate(event.nextInstance.startDate)
            : "Date TBD"}
        </Typography>
      </Box>

      <ChevronRightIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
    </ButtonBase>
  );
};

const LoadingSkeleton = () => (
  <Box sx={{ px: 2, py: 1.5 }}>
    {[0, 1, 2].map((i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Skeleton variant="rounded" width={64} height={64} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="70%" height={24} />
          <Skeleton variant="text" width="50%" height={20} />
        </Box>
      </Box>
    ))}
  </Box>
);

export const UpcomingEventsWidget = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getUpcomingEvents(5, 14).then((data) => {
      setEvents(data || []);
      setIsLoading(false);
    });
  }, []);

  if (!isLoading && events.length === 0) {
    return null;
  }

  return (
    <Paper elevation={3} sx={{ mt: 3, mb: 2, borderRadius: 3, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <CalendarTodayIcon color="primary" fontSize="small" />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          Upcoming Events
        </Typography>
      </Box>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          {events.map((event, index) => (
            <React.Fragment key={event.id}>
              {index > 0 && <Divider sx={{ mx: 2 }} />}
              <EventRow event={event} />
            </React.Fragment>
          ))}
        </>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", pb: 2, pt: 1 }}>
        <Button
          component={RouterLink}
          to="/events"
          variant="text"
          color="primary"
          size="small"
          sx={{ fontWeight: 600 }}
        >
          View All Events
        </Button>
      </Box>
    </Paper>
  );
};
